import type {
  CatalogWorkerRequest,
  CatalogWorkerResponse,
  SearchDocument,
} from '@der-maschinfuehrer/catalog/worker-types';
import {
  canonicalJsonStringify,
  sha256HexBytes,
  sha256HexUtf8,
} from '@der-maschinfuehrer/contracts';

export const CATALOG_WORKER_PROTOCOL = 'dmf.catalog-worker.v1' as const;
export const CATALOG_SEARCH_DOCUMENT_SCHEMA = 'dmf.catalog-search-documents.v1' as const;

const CATALOG_UUID_SCHEMA = 'dmf.catalog.uuid.v1' as const;
const MAX_DOCUMENTS = 100_000;

export interface CatalogArtifactInitializeRequest {
  readonly protocol: typeof CATALOG_WORKER_PROTOCOL;
  readonly requestId: string;
  readonly type: 'initialize_artifact';
  readonly localProfileId: string;
  readonly generation: number;
  readonly artifactUrl: string;
  readonly catalogVersion: string;
  readonly formatVersion: string;
  readonly documentCount: number;
  readonly schemaVersion: typeof CATALOG_SEARCH_DOCUMENT_SCHEMA;
}

export type CatalogAppWorkerRequest =
  CatalogArtifactInitializeRequest | Exclude<CatalogWorkerRequest, { readonly type: 'initialize' }>;

type CatalogUsage = Extract<CatalogWorkerRequest, { readonly type: 'set_usage' }>['usage'];
type CatalogWorkerErrorResponse = Extract<CatalogWorkerResponse, { readonly type: 'error' }>;

interface CatalogArtifact {
  readonly documents: readonly unknown[];
}

interface ActiveCatalogBinding {
  readonly localProfileId: string;
  readonly generation: number;
  readonly artifactUrl: string;
  readonly catalogVersion: string;
  readonly formatVersion: string;
  readonly documentCount: number;
  readonly schemaVersion: typeof CATALOG_SEARCH_DOCUMENT_SCHEMA;
  readonly documentIdBySnapshotLocationId: ReadonlyMap<string, string>;
}

interface PendingCatalogBinding {
  readonly localProfileId: string;
  readonly generation: number;
}

export interface CatalogWorkerEnginePort {
  handle(request: CatalogWorkerRequest): CatalogWorkerResponse;
}

export interface CatalogArtifactWorkerDependencies {
  readonly createEngine: () => CatalogWorkerEnginePort;
  readonly fetchArtifact: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  readonly parseRequest: (input: unknown) => CatalogWorkerRequest;
}

export class CatalogArtifactWorkerRuntime {
  readonly #createEngine: CatalogArtifactWorkerDependencies['createEngine'];
  #engine: CatalogWorkerEnginePort;
  readonly #fetchArtifact: CatalogArtifactWorkerDependencies['fetchArtifact'];
  readonly #parseRequest: CatalogArtifactWorkerDependencies['parseRequest'];
  #active: ActiveCatalogBinding | null = null;
  #pending: PendingCatalogBinding | null = null;

  public constructor(dependencies: CatalogArtifactWorkerDependencies) {
    this.#createEngine = dependencies.createEngine;
    this.#engine = dependencies.createEngine();
    this.#fetchArtifact = dependencies.fetchArtifact;
    this.#parseRequest = dependencies.parseRequest;
  }

  public async handleUnknown(input: unknown): Promise<CatalogWorkerResponse> {
    if (isRecord(input) && input.type === 'initialize_artifact') {
      return this.#initializeArtifact(input);
    }
    if (isRecord(input) && input.type === 'initialize') {
      return errorResponse(
        requestIdFrom(input),
        'invalid_message',
        'Katalog lze inicializovat pouze z připnutého neměnného artefaktu.',
      );
    }

    let request: CatalogWorkerRequest;
    try {
      request = this.#parseRequest(input);
    } catch {
      return errorResponse(
        requestIdFrom(input),
        'invalid_message',
        'Katalogový Worker odmítl neplatný požadavek.',
      );
    }
    if (request.type === 'initialize') {
      return errorResponse(
        request.requestId,
        'invalid_message',
        'Katalog lze inicializovat pouze z připnutého neměnného artefaktu.',
      );
    }

    const mappedRequest =
      request.type === 'set_usage'
        ? {
            ...request,
            usage: mapPersistedUsageToWorkerDocuments(
              request.usage,
              this.#active?.documentIdBySnapshotLocationId ?? new Map(),
            ),
          }
        : request;
    const response = this.#handleCore(mappedRequest);
    if (request.type === 'dispose' && response.type === 'ack') {
      this.#active = null;
    }
    return response;
  }

  async #initializeArtifact(input: Record<string, unknown>): Promise<CatalogWorkerResponse> {
    let request: CatalogArtifactInitializeRequest;
    try {
      request = parseCatalogArtifactInitializeRequest(input);
    } catch {
      return errorResponse(
        requestIdFrom(input),
        'invalid_message',
        'Katalogový Worker odmítl neplatné údaje připnutého artefaktu.',
      );
    }

    const bindingResponse = this.#checkInitializationBinding(request);
    if (bindingResponse !== null) return bindingResponse;
    if (this.#active !== null && sameActiveArtifact(this.#active, request)) {
      return readyResponse(request);
    }

    const pending = {
      localProfileId: request.localProfileId,
      generation: request.generation,
    };
    this.#pending = pending;
    try {
      const artifact = await loadCatalogArtifact(request, this.#fetchArtifact);
      const candidateEngine = this.#createEngine();
      let initializeRequest: Extract<CatalogWorkerRequest, { readonly type: 'initialize' }>;
      let ready: CatalogWorkerResponse;
      try {
        const parsedRequest = this.#parseRequest({
          protocol: CATALOG_WORKER_PROTOCOL,
          requestId: request.requestId,
          type: 'initialize',
          localProfileId: request.localProfileId,
          generation: request.generation,
          catalogVersion: request.catalogVersion,
          documents: artifact.documents,
        });
        if (parsedRequest.type !== 'initialize') {
          return artifactErrorResponse(request.requestId, 'invalid_artifact');
        }
        initializeRequest = parsedRequest;
        ready = candidateEngine.handle(initializeRequest);
      } catch {
        return artifactErrorResponse(request.requestId, 'invalid_artifact');
      }
      if (ready.type === 'error') return localizeCoreError(ready);
      if (
        ready.type !== 'ready' ||
        ready.catalogVersion !== request.catalogVersion ||
        ready.localProfileId !== request.localProfileId ||
        ready.generation !== request.generation ||
        ready.documentCount !== request.documentCount
      ) {
        return artifactErrorResponse(request.requestId, 'invalid_artifact');
      }

      let documentIdBySnapshotLocationId: ReadonlyMap<string, string>;
      try {
        documentIdBySnapshotLocationId = indexSnapshotLocationIds(initializeRequest.documents);
      } catch {
        return artifactErrorResponse(request.requestId, 'invalid_artifact');
      }
      this.#engine = candidateEngine;
      this.#active = {
        localProfileId: request.localProfileId,
        generation: request.generation,
        artifactUrl: request.artifactUrl,
        catalogVersion: request.catalogVersion,
        formatVersion: request.formatVersion,
        documentCount: request.documentCount,
        schemaVersion: request.schemaVersion,
        documentIdBySnapshotLocationId,
      };
      return ready;
    } catch (error) {
      return artifactErrorResponse(
        request.requestId,
        error instanceof CatalogArtifactError ? error.code : 'invalid_artifact',
      );
    } finally {
      if (this.#pending === pending) this.#pending = null;
    }
  }

  #checkInitializationBinding(
    request: CatalogArtifactInitializeRequest,
  ): CatalogWorkerResponse | null {
    const pending = this.#pending;
    if (pending !== null) {
      if (request.localProfileId !== pending.localProfileId) {
        return errorResponse(
          request.requestId,
          'profile_mismatch',
          'Katalogový Worker je svázaný s jiným lokálním profilem.',
        );
      }
      if (request.generation < pending.generation) {
        return errorResponse(
          request.requestId,
          'stale_generation',
          'Požadavek používá zastaralou generaci katalogu.',
        );
      }
      return errorResponse(
        request.requestId,
        'generation_mismatch',
        'Inicializace této generace katalogu už probíhá.',
      );
    }

    const active = this.#active;
    if (active === null) return null;
    if (request.localProfileId !== active.localProfileId) {
      return errorResponse(
        request.requestId,
        'profile_mismatch',
        'Katalogový Worker je svázaný s jiným lokálním profilem.',
      );
    }
    if (request.generation < active.generation) {
      return errorResponse(
        request.requestId,
        'stale_generation',
        'Požadavek používá zastaralou generaci katalogu.',
      );
    }
    if (request.generation === active.generation && !sameActiveArtifact(active, request)) {
      return errorResponse(
        request.requestId,
        'generation_mismatch',
        'Stejná generace katalogu nesmí změnit připnutý artefakt.',
      );
    }
    return null;
  }

  #handleCore(request: CatalogWorkerRequest): CatalogWorkerResponse {
    try {
      return localizeCoreError(this.#engine.handle(request));
    } catch {
      return errorResponse(
        request.requestId,
        request.type === 'query' ? 'query_failure' : 'invalid_message',
        request.type === 'query'
          ? 'Vyhledávání v katalogu selhalo. Ruční zadání zůstává dostupné.'
          : 'Katalogový Worker odmítl neplatnou operaci.',
      );
    }
  }
}

export function installCatalogArtifactWorker(
  scope: unknown,
  dependencies: Omit<CatalogArtifactWorkerDependencies, 'fetchArtifact'>,
): void {
  if (typeof scope !== 'object' || scope === null) {
    throw new TypeError('Catalog artifact Worker scope must be an object');
  }
  const candidate = scope as {
    readonly document?: unknown;
    readonly fetch?: CatalogArtifactWorkerDependencies['fetchArtifact'];
    readonly addEventListener?: (
      type: 'message',
      listener: (event: { readonly data: unknown }) => void,
    ) => void;
    readonly postMessage?: (response: CatalogWorkerResponse) => void;
  };
  if (
    candidate.document !== undefined ||
    typeof candidate.fetch !== 'function' ||
    typeof candidate.addEventListener !== 'function' ||
    typeof candidate.postMessage !== 'function'
  ) {
    throw new TypeError('Catalog artifact Worker requires a dedicated Worker scope');
  }
  const runtime = new CatalogArtifactWorkerRuntime({
    ...dependencies,
    fetchArtifact: candidate.fetch.bind(scope),
  });
  candidate.addEventListener('message', (event) => {
    void runtime
      .handleUnknown(event.data)
      .then((response) => candidate.postMessage?.(response))
      .catch(() =>
        candidate.postMessage?.(
          errorResponse(
            requestIdFrom(event.data),
            'invalid_message',
            'Katalogový Worker bezpečně odmítl neočekávané selhání.',
          ),
        ),
      );
  });
}

export function catalogLocationUuid(catalogLocationId: string): string {
  const semanticKey = canonicalJsonStringify({
    schema: CATALOG_UUID_SCHEMA,
    kind: 'location',
    key: { catalogLocationId },
  });
  const digest = sha256HexUtf8(semanticKey);
  const bytes = Uint8Array.from(
    Array.from({ length: 16 }, (_, index) =>
      Number.parseInt(digest.slice(index * 2, index * 2 + 2), 16),
    ),
  );
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x80;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const uuidHex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return [
    uuidHex.slice(0, 8),
    uuidHex.slice(8, 12),
    uuidHex.slice(12, 16),
    uuidHex.slice(16, 20),
    uuidHex.slice(20),
  ].join('-');
}

async function loadCatalogArtifact(
  request: CatalogArtifactInitializeRequest,
  fetchArtifact: CatalogArtifactWorkerDependencies['fetchArtifact'],
): Promise<CatalogArtifact> {
  let response: Response;
  try {
    response = await fetchArtifact(request.artifactUrl, {
      cache: 'default',
      credentials: 'omit',
      headers: { accept: 'application/json' },
    });
  } catch {
    throw new CatalogArtifactError('fetch_failed');
  }
  if (!response.ok) throw new CatalogArtifactError('fetch_failed');

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await response.arrayBuffer());
  } catch {
    throw new CatalogArtifactError('fetch_failed');
  }
  if (sha256HexBytes(bytes) !== request.catalogVersion.slice('sha256-'.length)) {
    throw new CatalogArtifactError('checksum_mismatch');
  }

  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new CatalogArtifactError('invalid_utf8');
  }
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    throw new CatalogArtifactError('invalid_json');
  }
  if (!isRecord(value) || !hasExactKeys(value, ['documents', 'format_version', 'schema_version'])) {
    throw new CatalogArtifactError('invalid_schema');
  }
  if (value.schema_version !== request.schemaVersion) {
    throw new CatalogArtifactError('invalid_schema');
  }
  if (value.format_version !== request.formatVersion) {
    throw new CatalogArtifactError('invalid_format');
  }
  if (!Array.isArray(value.documents) || value.documents.length !== request.documentCount) {
    throw new CatalogArtifactError('invalid_count');
  }
  return { documents: value.documents };
}

function parseCatalogArtifactInitializeRequest(
  input: Record<string, unknown>,
): CatalogArtifactInitializeRequest {
  if (
    !hasExactKeys(input, [
      'protocol',
      'requestId',
      'type',
      'localProfileId',
      'generation',
      'artifactUrl',
      'catalogVersion',
      'formatVersion',
      'documentCount',
      'schemaVersion',
    ]) ||
    input.protocol !== CATALOG_WORKER_PROTOCOL ||
    input.type !== 'initialize_artifact'
  ) {
    throw new TypeError('Invalid catalog artifact initialization envelope');
  }
  const requestId = boundedString(input.requestId, 128);
  const localProfileId = boundedString(input.localProfileId, 128);
  const catalogVersion = boundedString(input.catalogVersion, 128);
  const artifactUrl = boundedString(input.artifactUrl, 512);
  const formatVersion = boundedString(input.formatVersion, 128);
  if (!Number.isSafeInteger(input.generation) || (input.generation as number) < 1) {
    throw new TypeError('Invalid catalog generation');
  }
  if (
    !Number.isSafeInteger(input.documentCount) ||
    (input.documentCount as number) < 1 ||
    (input.documentCount as number) > MAX_DOCUMENTS
  ) {
    throw new TypeError('Invalid catalog document count');
  }
  if (
    !/^sha256-[a-f0-9]{64}$/.test(catalogVersion) ||
    artifactUrl !== `/app/artifacts/catalog/${catalogVersion}/catalog.json` ||
    input.schemaVersion !== CATALOG_SEARCH_DOCUMENT_SCHEMA
  ) {
    throw new TypeError('Catalog artifact identity is not canonical');
  }
  return {
    protocol: CATALOG_WORKER_PROTOCOL,
    requestId,
    type: 'initialize_artifact',
    localProfileId,
    generation: input.generation as number,
    artifactUrl,
    catalogVersion,
    formatVersion,
    documentCount: input.documentCount as number,
    schemaVersion: CATALOG_SEARCH_DOCUMENT_SCHEMA,
  };
}

function indexSnapshotLocationIds(
  documents: readonly SearchDocument[],
): ReadonlyMap<string, string> {
  const documentIdBySnapshotLocationId = new Map<string, string>();
  for (const document of documents) {
    const snapshotId = catalogLocationUuid(document.id);
    const previousDocumentId = documentIdBySnapshotLocationId.get(snapshotId);
    if (previousDocumentId !== undefined && previousDocumentId !== document.id) {
      throw new TypeError('Catalog snapshot UUID collision');
    }
    documentIdBySnapshotLocationId.set(snapshotId, document.id);
  }
  return documentIdBySnapshotLocationId;
}

function mapPersistedUsageToWorkerDocuments(
  usage: CatalogUsage,
  documentIdBySnapshotLocationId: ReadonlyMap<string, string>,
): CatalogUsage {
  const favoriteLocationIds = [
    ...new Set(
      usage.favoriteLocationIds.flatMap((locationId) => {
        const documentId = documentIdBySnapshotLocationId.get(locationId);
        return documentId === undefined ? [] : [documentId];
      }),
    ),
  ].sort(compareCodeUnits);
  const recentByDocumentId = new Map<string, number>();
  for (const recent of usage.recents) {
    const documentId = documentIdBySnapshotLocationId.get(recent.locationId);
    if (documentId === undefined) continue;
    recentByDocumentId.set(
      documentId,
      Math.max(recentByDocumentId.get(documentId) ?? 0, recent.lastUsedAtEpochMs),
    );
  }
  return {
    favoriteLocationIds,
    recents: [...recentByDocumentId]
      .sort(([left], [right]) => compareCodeUnits(left, right))
      .map(([locationId, lastUsedAtEpochMs]) => ({ locationId, lastUsedAtEpochMs })),
  };
}

function sameActiveArtifact(
  active: ActiveCatalogBinding,
  request: CatalogArtifactInitializeRequest,
): boolean {
  return (
    active.localProfileId === request.localProfileId &&
    active.generation === request.generation &&
    active.artifactUrl === request.artifactUrl &&
    active.catalogVersion === request.catalogVersion &&
    active.formatVersion === request.formatVersion &&
    active.documentCount === request.documentCount &&
    active.schemaVersion === request.schemaVersion
  );
}

function readyResponse(
  request: CatalogArtifactInitializeRequest,
): Extract<CatalogWorkerResponse, { readonly type: 'ready' }> {
  return {
    protocol: CATALOG_WORKER_PROTOCOL,
    requestId: request.requestId,
    type: 'ready',
    catalogVersion: request.catalogVersion,
    localProfileId: request.localProfileId,
    generation: request.generation,
    documentCount: request.documentCount,
  };
}

function localizeCoreError(response: CatalogWorkerResponse): CatalogWorkerResponse {
  if (response.type !== 'error') return response;
  const messages: Record<CatalogWorkerErrorResponse['code'], string> = {
    invalid_message: 'Katalogový Worker odmítl neplatnou operaci.',
    not_initialized: 'Katalog ještě není bezpečně připravený. Ruční zadání zůstává dostupné.',
    profile_mismatch: 'Katalogový Worker je svázaný s jiným lokálním profilem.',
    stale_generation: 'Požadavek používá zastaralou generaci katalogu.',
    generation_mismatch: 'Požadavek nepatří k aktivní generaci katalogu.',
    query_failure: 'Vyhledávání v katalogu selhalo. Ruční zadání zůstává dostupné.',
  };
  return { ...response, message: messages[response.code] };
}

type CatalogArtifactErrorCode =
  | 'fetch_failed'
  | 'checksum_mismatch'
  | 'invalid_utf8'
  | 'invalid_json'
  | 'invalid_schema'
  | 'invalid_format'
  | 'invalid_count'
  | 'invalid_artifact';

class CatalogArtifactError extends Error {
  public constructor(public readonly code: CatalogArtifactErrorCode) {
    super(code);
    this.name = 'CatalogArtifactError';
  }
}

function artifactErrorResponse(
  requestId: string,
  code: CatalogArtifactErrorCode,
): CatalogWorkerErrorResponse {
  const message =
    code === 'fetch_failed'
      ? 'Katalog se nepodařilo bezpečně načíst. Ruční zadání zůstává dostupné.'
      : code === 'checksum_mismatch'
        ? 'Katalog neprošel kontrolou integrity. Ruční zadání zůstává dostupné.'
        : 'Katalogový soubor nemá očekávaný bezpečný formát. Ruční zadání zůstává dostupné.';
  return errorResponse(requestId, 'invalid_message', message);
}

function errorResponse(
  requestId: string,
  code: CatalogWorkerErrorResponse['code'],
  message: string,
): CatalogWorkerErrorResponse {
  return {
    protocol: CATALOG_WORKER_PROTOCOL,
    requestId,
    type: 'error',
    code,
    message,
  };
}

function requestIdFrom(value: unknown): string {
  if (!isRecord(value)) return 'invalid';
  try {
    return boundedString(value.requestId, 128);
  } catch {
    return 'invalid';
  }
}

function boundedString(value: unknown, maximum: number): string {
  if (typeof value !== 'string' || value.trim().length === 0 || value.length > maximum) {
    throw new TypeError('Expected a bounded non-empty string');
  }
  return value;
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const actual = Object.keys(value).sort(compareCodeUnits);
  const sortedExpected = [...expected].sort(compareCodeUnits);
  return (
    actual.length === sortedExpected.length &&
    actual.every((key, index) => key === sortedExpected[index])
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
